import { useEffect, useRef } from 'react';
import type { HwChatElement } from '../element/HwChat.js';
import '../element/HwChat.js'; // side-effect: registers <hw-chat>
import type { AuthProvider } from '../core/types.js';

export interface HwChatProps {
  chatId: string;
  apiBase?: string;
  authEndpoint?: string;
  authProvider?: AuthProvider;
  theme?: 'dark' | 'light' | 'auto';
  primaryColor?: string;
  /**
   * When set, a mic button appears in the header and clicking it
   * starts a live voice call against this provider-side agent id.
   */
  voiceAgentId?: string;
  /** Voice agent platform — defaults to 'elevenlabs'. */
  voiceProvider?: 'elevenlabs' | 'vapi' | 'retell';
  /** Override the CDN URL the widget loads the voice SDK from. */
  voiceSdkUrl?: string;
  /** Pass through a style object — the Web Component is a block element. */
  style?: React.CSSProperties;
  className?: string;
}

export function HwChat({
  chatId,
  apiBase,
  authEndpoint,
  authProvider,
  theme,
  primaryColor,
  voiceAgentId,
  voiceProvider,
  voiceSdkUrl,
  style,
  className,
}: HwChatProps): JSX.Element {
  const ref = useRef<HwChatElement | null>(null);

  // authProvider is a function — can't ride on an HTML attribute. Assign as
  // a property after the element mounts, and whenever the provider changes.
  useEffect(() => {
    if (!ref.current) return;
    ref.current.authProvider = authProvider ?? null;
  }, [authProvider]);

  // React 18 doesn't know about custom element attributes by default, so we
  // bypass its type-checker with a type-cast on the intrinsic element.
  const Tag = 'hw-chat' as unknown as 'div';

  return (
    <Tag
      ref={ref as any}
      // camelCase → kebab-case attributes the Web Component observes
      {...{
        'chat-id': chatId,
        ...(apiBase ? { 'api-base': apiBase } : {}),
        ...(authEndpoint ? { 'auth-endpoint': authEndpoint } : {}),
        ...(theme ? { theme } : {}),
        ...(primaryColor ? { 'primary-color': primaryColor } : {}),
        ...(voiceAgentId ? { 'voice-agent-id': voiceAgentId } : {}),
        ...(voiceProvider ? { 'voice-provider': voiceProvider } : {}),
        ...(voiceSdkUrl ? { 'voice-sdk-url': voiceSdkUrl } : {}),
      }}
      style={style}
      className={className}
    />
  );
}
